import type { SportWithTeams } from '../types';

type OrgTeam = SportWithTeams['teams'][number];

const currentPeopleCount = (team: OrgTeam): number =>
  team.people.filter((p) => p.endDate == null).length;

const currentSportPeopleCount = (sport: SportWithTeams): number =>
  sport.people.filter((p) => p.endDate == null).length;

/**
 * "Empty" = a team with no one on it. Specifically, a Blast team (blastTeamId
 * set) whose Blast roster is 0 AND which has no *current* CRM-linked person.
 * CRM-only teams (no blastTeamId — i.e. a rep made it) are never auto-hidden.
 * A null (not-yet-computed) roster is treated as content (fail open).
 *
 * NOTE: counts *current* people only (endDate == null). The org payload now
 * always includes former/end-dated links, so counting all people would let a
 * team with only departed coaches dodge the empty filter.
 *
 * This is the ROSTER axis. It is independent of the recency axis (isActive):
 * a team can be empty whether it's current or former.
 */
export function isHiddenTeam(team: OrgTeam): boolean {
  if (team.blastTeamId === null) return false;
  const hasRoster = team.rosterCount === null ? true : team.rosterCount > 0;
  return !hasRoster && currentPeopleCount(team) === 0;
}

/**
 * A sport is fully hidden only when it HAS teams, every one of them is empty,
 * and it has no current program-staff people. A sport with at least one team
 * that has content (current OR former-with-roster) stays visible — its former
 * teams surface under the per-sport "former teams" reveal.
 */
export function isHiddenSport(sport: SportWithTeams): boolean {
  return (
    sport.teams.length > 0 &&
    currentSportPeopleCount(sport) === 0 &&
    sport.teams.every(isHiddenTeam)
  );
}

/**
 * Drop fully-empty sports when showEmpty is off. Does NOT strip teams — the
 * per-sport current / former / empty bucketing is owned by SportCard, which
 * decides what to render given the showEmptyTeams flag. When showEmpty is true,
 * everything passes through.
 */
export function visibleSports(sports: SportWithTeams[], showEmpty: boolean): SportWithTeams[] {
  if (showEmpty) return sports;
  return sports.filter((s) => !isHiddenSport(s));
}

/** Count of empty teams and fully-empty sports (for the "Show empty teams" label). */
export function hiddenCounts(sports: SportWithTeams[]): { teams: number; sports: number } {
  let teams = 0;
  let sportCount = 0;
  for (const s of sports) {
    teams += s.teams.filter(isHiddenTeam).length;
    if (isHiddenSport(s)) sportCount += 1;
  }
  return { teams, sports: sportCount };
}
